import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { MotifTable } from '@ey-xd/motif-react';
import {
  handleFilterChanged,
  handleGridReady,
  handleItemsPerPageChange,
  handleFirstDataRendered,
} from '../../common/AgGridEventHandlers';
import CustomPagination from '../../common/MotifPagination';
import MasterDataManagementGroupsCellRenderer from '../shared/customRenderers/MasterDataManagementGroupsCellRenderer';
import CustomHeaderTemplate from '../shared/customHeaders/CustomHeaderTemplate';
import DateComparator from '../shared/customComparators/DateComparator';
import DateCellRenderer from '../checklistDeliveryCalendar/customRenderers/DateCellRenderer';

const GroupsTable = ({
  groupsData,
  handleEditButtonClick,
  handleDeleteButtonClick,
  handleGridChanges,
  gridColumnState,
  refreshGridGroupData,
}) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const getRowId = useCallback((params) => params.data.entityGroupID, []);

  const dateOptions = {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };

  //Default column settings
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 65,
      filter: true,
      resizable: true,
      sortable: true,
      wrapText: true,
      autoHeight: true,
    };
  }, []);

  // Instruction text for the overlay
  const instructionText =
    'Click on Actions > Click Export Group Import Template > Update the downloaded file with the Groups details > Click Upload Group Import Template.';

  const columnDefs = useMemo(
    () => [
      {
        field: 'entityGroupName',
        headerName: 'Group Name',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 350,
        valueGetter: (params) => {
          return params.data?.entityGroupName || '';
        },
        headerComponentParams: { template: CustomHeaderTemplate('GroupName') },
      },
      {
        field: 'reitCount',
        headerName: 'REIT Count',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 350,
        valueGetter: (params) => {
          return params.data?.reitCount || 0;
        },
        headerComponentParams: { template: CustomHeaderTemplate('REITCount') },
      },
      {
        field: 'lastUpdatedBy',
        headerName: 'Last Updated By',
        sortable: true,
        minWidth: 185,
        hide: true,
        headerComponentParams: {
          template: CustomHeaderTemplate('LastUpdatedBy'),
        },
      },
      {
        field: 'lastUpdatedDateTime',
        headerName: 'Last Updated Date',
        sortable: true,
        minWidth: 200,
        hide: true,
        cellRenderer: (params) => {
          // Don't render date for default group row
          if (params.data?.entityGroupID === -1) {
            return '';
          }
          // Use the DateCellRenderer for all other rows
          return DateCellRenderer(params);
        },
        cellRendererParams: { dateFieldName: 'LastUpdatedDateTime' },
        filterValueGetter: (params) => {
          // Hide lastUpdatedDateTime when entityGroupID is -1
          if (params.data?.entityGroupID === -1) {
            return '';
          }
          const dateValue =
            params.data && params.data.lastUpdatedDateTime
              ? params.data.lastUpdatedDateTime
              : null;
          return dateValue
            ? new Date(dateValue + 'Z').toLocaleDateString('en-US', dateOptions)
            : '';
        },
        comparator: DateComparator,
        headerComponentParams: {
          template: CustomHeaderTemplate('LastUpdatedDate'),
        },
      },
      {
        field: 'editactions',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        suppressSeparator: true,
        sortable: false,
        minWidth: 70,
        maxWidth: 70,
        cellRenderer: (params) => {
          if (params.data?.entityGroupID === -1) {
            return null;
          }
          return <MasterDataManagementGroupsCellRenderer {...params} />;
        },
        cellRendererParams: { handleEditButtonClick },
      },
      {
        field: 'deleteactions',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        suppressSeparator: true,
        sortable: false,
        minWidth: 70,
        maxWidth: 70,
        cellRenderer: (params) => {
          if (params.data?.entityGroupID === -1) {
            return null;
          }
          return <MasterDataManagementGroupsCellRenderer {...params} />;
        },
        cellRendererParams: { handleDeleteButtonClick },
      },
    ],
    [handleEditButtonClick, handleDeleteButtonClick]
  );

  useEffect(() => {
    if (rowData.length > 0 && gridRef.current.api !== undefined) {
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
      //If total page is zero then set the current page as zero
      if (calcTotalPages == 0) {
        setCurrentPage(calcTotalPages);
      }
    }
  }, [rowData]);

  useEffect(() => {
    setRowData(groupsData);
    if (gridRef && gridRef.current && gridRef.current.api) {
      // Get total pages from Grid's default pagination control and apply it to custom pagination control
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);

      gridRef.current.api.refreshCells();
    }
  }, [groupsData]);

  useEffect(() => {
    handleGridChanges(
      rowData,
      gridRef && gridRef.current ? gridRef.current.api : null
    );
  }, [rowData]);

  useEffect(() => {
    //Reset Grid to default column state
    //When Grid Column state changes due to Reset Column State action then Reset Grid to default column state
    let localStorageColumnState = localStorage.getItem(
      'REITSuiteGroupDataColumnState'
    );
    if (
      !localStorageColumnState &&
      gridRef &&
      gridRef.current &&
      gridRef.current.api
    ) {
      gridRef.current.api.setColumnDefs([]);
      gridRef.current.api.setColumnDefs(columnDefs);
    }
  }, [gridColumnState]);

  //Handle Grid Page change event
  const handlePageChange = useCallback((val) => {
    setCurrentPage(val);
    gridRef.current.api.paginationGoToPage(val - 1);
  }, []);
  const rowHeight = 73;
  const headerHeight = 45;
  const totalHeight = 5 * rowHeight + headerHeight; // set 5 rows as default

  const overlayNoRowsTemplate = `<div class="custom-no-rows-template">${instructionText}</div>`;
  const hasData = groupsData && groupsData.length > 0;

  const onCellKeyDown = useCallback(
    (event) => {
      // First check if this row should have actions hidden
      if (event.data?.entityGroupID === -1) {
        return; // Exit early if this row's actions should be hidden
      }
      if (event.event.key === 'Enter' || event.event.key === ' ') {
        if (event.colDef.field == 'editactions') {
          handleEditButtonClick(event.data);
        }
        if (event.colDef.field == 'deleteactions') {
          handleDeleteButtonClick(event.data);
        }
      }
    },
    [handleEditButtonClick, handleDeleteButtonClick]
  );

  return (
    <React.Fragment>
      <div className="row mb-10">
        <div className="col mt-3">
          <div
            data-testid="grdGroupsDataGrid"
            className={`ag-theme-quartz motif-table ${hasData ? 'table-bd-full-height-width' : 'empty-table-container'}`}
          >
            <MotifTable
              zebra={true}
              compact={true}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowId={getRowId}
              accentedSort={true}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              onGridReady={handleGridReady}
              onFirstDataRendered={handleFirstDataRendered}
              ref={gridRef}
              pagination={true}
              suppressPaginationPanel={true}
              paginationPageSize={100}
              groupDisplayType={'custom'}
              groupSelectsChildren={true}
              groupDefaultExpanded={0}
              onFilterChanged={handleFilterChanged(
                gridRef,
                currentPage,
                setCurrentPage,
                setTotalPages
              )}
              overlayNoRowsTemplate={overlayNoRowsTemplate}
              onCellKeyDown={onCellKeyDown}
            />

            {rowData.length > 0 && (
              <div className="mt-3">
                <span className="right mb-3">
                  <CustomPagination
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                    totalPages={totalPages}
                    handleItemsPerPageChange={handleItemsPerPageChange}
                    gridRef={gridRef}
                    setCurrentPage={setCurrentPage}
                    setItemsPerPage={setItemsPerPage}
                    setTotalPages={setTotalPages}
                  />
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

GroupsTable.propTypes = {
  groupsData: PropTypes.array.isRequired,
  handleEditButtonClick: PropTypes.func.isRequired,
  handleDeleteButtonClick: PropTypes.func.isRequired,
  handleGridChanges: PropTypes.func.isRequired,
  gridColumnState: PropTypes.object.isRequired,
  refreshGridGroupData: PropTypes.func.isRequired,
};

export default GroupsTable;
